import React, { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tailwind doesn't allow fully dynamic class names; map color id → static classes.
const BANNER_COLORS = {
  orange: { bg: 'bg-orange-700', text: 'text-orange-50', hover: 'hover:bg-orange-800' },
  yellow: { bg: 'bg-yellow-500',  text: 'text-yellow-50',  hover: 'hover:bg-yellow-600'  },
  green:  { bg: 'bg-green-700',  text: 'text-green-50',  hover: 'hover:bg-green-800'  },
  blue:   { bg: 'bg-blue-700',   text: 'text-blue-50',   hover: 'hover:bg-blue-800'   },
  red:    { bg: 'bg-red-700',    text: 'text-red-50',    hover: 'hover:bg-red-800'    },
};

/**
 * Top-of-app fixed banner showing scheduled maintenance notice.
 * - Polls /api/public/maintenance-notice every 5 min
 * - Yellow dark, full-width, fixed at the very top (above sidebar)
 * - User can dismiss with X; reappears on refresh (session-only via useState)
 */
export default function MaintenanceBanner() {
  const [notice, setNotice] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const fetchNotice = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/maintenance-notice`);
      if (!res.ok) return;
      const data = await res.json();
      if (data?.active) {
        setNotice(data);
      } else {
        setNotice(null);
      }
    } catch (_) {
      // silent: banner is non-critical
    }
  };

  useEffect(() => {
    fetchNotice();
    const t = setInterval(fetchNotice, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (!notice || dismissed) return null;

  const colors = BANNER_COLORS[notice.color] || BANNER_COLORS.yellow;

  const fmt = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[100] ${colors.bg} ${colors.text} shadow-md`}
      data-testid="maintenance-banner"
    >
      <div className="flex items-center gap-3 px-4 py-2.5 text-sm">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        <div className="flex-1 leading-snug">
          <span className="font-semibold">Manutenção programada</span>
          {notice.starts_at && (
            <> &middot; {fmt(notice.starts_at)} até {fmt(notice.ends_at)}</>
          )}
          <span className="ml-2 opacity-90">{notice.message}</span>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className={`p-1 rounded ${colors.hover} transition-colors`}
          aria-label="Dispensar aviso"
          data-testid="maintenance-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
